'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useQueryState } from 'nuqs';
import { useEffect } from 'react';
import { KlaviyoForm } from './KlaviyoForm';

interface KlaviyoModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
}

// Global type definition for Klaviyo
declare global {
  interface Window {
    _klOnsite: any[];
    klaviyo: any;
    _klForms: string[];
  }
}

export function KlaviyoModal() {
  const [isOpen, setIsOpen] = useQueryState<boolean>('klaviyo', {
    history: 'replace',
    parse: (value: string | null) => value === 'true',
    serialize: (value: boolean) => value.toString(),
  });
  const [formId, setFormId] = useQueryState('formId', {
    defaultValue: '',
  });

  useEffect(() => {
    // Add click handler to document to listen for Klaviyo links
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const klaviyoLink = target.closest('[data-klaviyo-form]');

      if (klaviyoLink) {
        e.preventDefault();
        const formId = klaviyoLink.getAttribute('data-klaviyo-form');
        if (formId) {
          setFormId(formId);
          setIsOpen(true);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [setFormId, setIsOpen]);

  if (!isOpen) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => setIsOpen(false)}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <Dialog.Title className="DialogTitle">Get Tickets</Dialog.Title>
        <Dialog.Description className="DialogDescription">
          Sign up to be notified when tickets go on sale!
        </Dialog.Description>
        <Dialog.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed left-[50%] top-[50%] z-50 grid max-h-[90vh] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-auto border bg-white p-6 shadow-lg duration-200 sm:rounded-lg">
          {/* Use the iframe-based KlaviyoForm component */}
          <div className="relative min-h-[300px]">
            {isOpen && <KlaviyoForm id={formId} />}
          </div>
          <Dialog.Close className="focus:ring-slate-950 data-[state=open]:bg-slate-100 data-[state=open]:text-slate-500 absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            <span className="sr-only">Close</span>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
